import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {GlobalConstants} from "../../../../common/global-constants";

/**
 * Data passed into the edit dialog. identifiers and transitTimeType are read-only
 * (transit time type is changed on the "Switch Transit Time Type" page). Only
 * mappedServices is editable here. existingRecords is the current table set, used
 * for the own-record client-side pre-check.
 */
export interface EditTransitTimeConfigData {
  businessUnit: string;
  fromCountryCode: string;
  componentKeyValue: string;
  transitTimeType: string;
  mappedServices: string[];
  updatedBy: string;
  existingRecords: any[];
}

@Component({
  selector: 'app-edit-transit-time-config',
  templateUrl: './edit-transit-time-config.component.html',
  styleUrls: ['./edit-transit-time-config.component.scss']
})
export class EditTransitTimeConfigComponent implements OnInit {

  mappedServices: string[] = [];
  newMappedService = '';

  private originalMappedServices: string[] = [];

  // Suffix of this record's own componentKeyValue, and the suffixes of all
  // sibling records, used for the self-reference / own-record pre-checks.
  private parentSuffix: string;
  private existingSuffixes: Set<string>;

  addError = '';
  inlineError = '';
  saving = false;
  // When true, the dialog shows the inline confirmation step instead of the edit
  // form (avoids stacking a second dialog on top of this one).
  confirming = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditTransitTimeConfigData,
    private dialogRef: MatDialogRef<EditTransitTimeConfigComponent>,
    @Inject('RequestsService') private requestsService) {
  }

  ngOnInit() {
    this.mappedServices = this.data.mappedServices ? [...this.data.mappedServices] : [];
    this.originalMappedServices = this.data.mappedServices ? [...this.data.mappedServices] : [];

    this.parentSuffix = this.getSuffix(this.data.componentKeyValue);
    this.existingSuffixes = new Set(
      (this.data.existingRecords || [])
        .map(record => this.getSuffix(record[GlobalConstants.COMPONENT_KEY_VALUE]))
        .filter(Boolean)
    );
  }

  private getSuffix(componentKeyValue: string): string {
    if (!componentKeyValue) {
      return '';
    }
    const parts = componentKeyValue.split(GlobalConstants.COMPONENT_KEY_VALUE_SEPARATOR);
    return parts[parts.length - 1];
  }

  public addMappedService() {
    this.addError = '';
    const child = (this.newMappedService || '').trim();
    if (!child) {
      return;
    }
    if (this.mappedServices.includes(child)) {
      this.addError = 'This ship method is already mapped';
      return;
    }
    if (child === this.parentSuffix) {
      this.addError = 'A ship method cannot be mapped to itself';
      return;
    }
    if (this.existingSuffixes.has(child)) {
      this.addError = `Mapped service ${child} already has its own configuration record `
        + `— a mapped service must not be a configured route itself`;
      return;
    }
    this.mappedServices = [...this.mappedServices, child];
    this.newMappedService = '';
  }

  public removeMappedService(child: string) {
    this.mappedServices = this.mappedServices.filter(service => service !== child);
  }

  public get addedServices(): string[] {
    return this.mappedServices.filter(service => !this.originalMappedServices.includes(service));
  }

  public get removedServices(): string[] {
    return this.originalMappedServices.filter(service => !this.mappedServices.includes(service));
  }

  public hasChanges(): boolean {
    const current = [...this.mappedServices].sort();
    const original = [...this.originalMappedServices].sort();
    if (current.length !== original.length) {
      return true;
    }
    return current.some((value, index) => value !== original[index]);
  }

  // Move from the edit form to the inline confirmation step.
  public save() {
    this.inlineError = '';
    if (!this.hasChanges()) {
      this.inlineError = 'No changes to save';
      return;
    }
    this.confirming = true;
  }

  // Return from the confirmation step to the edit form.
  public back() {
    this.confirming = false;
  }

  public confirmSave() {
    const requestBody: any = {};
    requestBody[GlobalConstants.BUSINESS_UNIT] = this.data.businessUnit;
    requestBody[GlobalConstants.FROM_COUNTRY_CODE] = this.data.fromCountryCode;
    requestBody[GlobalConstants.COMPONENT_KEY_VALUE] = this.data.componentKeyValue;
    requestBody[GlobalConstants.UPDATED_BY] = this.data.updatedBy || GlobalConstants.UNKNOWN_USER;
    // Only mappedServices is editable on this page; always send it (an empty list clears all).
    requestBody[GlobalConstants.MAPPED_SERVICES] = this.mappedServices;

    this.saving = true;
    this.requestsService.put(GlobalConstants.UPDATE_TRANSIT_TIME_CONFIGURATION_ENDPOINT, requestBody).subscribe((data: any) => {
      this.saving = false;
      if (data && data[GlobalConstants.STATUS_KEY] === GlobalConstants.STATUS_SUCCEEDED) {
        this.dialogRef.close({saved: true, message: data[GlobalConstants.MESSAGE_KEY]});
      } else {
        // Return to the edit form and show the backend message inline.
        this.confirming = false;
        this.inlineError = (data && data[GlobalConstants.MESSAGE_KEY]) || GlobalConstants.REQUEST_FAILED;
      }
    }, error => {
      this.saving = false;
      this.confirming = false;
      this.inlineError = (error && error.message) || GlobalConstants.SOMETHING_WENT_WRONG;
    });
  }

  public cancel() {
    this.dialogRef.close();
  }
}
